import React, { useState, useEffect, useCallback } from 'react';
import {
  Paper,
  Typography,
  Box,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TablePagination,
  IconButton,
  Tooltip,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  Card,
  CardContent,
  CardActions,
  Divider,
  Checkbox,
  Stack,
  Avatar,
  Badge,
  Fab,
  Slide,
  Zoom,
  Container,
  AppBar,
  Toolbar,
  useTheme,
  alpha,
  ButtonGroup,
  LinearProgress,
  Skeleton,
  Alert
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import {
  FilterList as FilterListIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  DoneAll as DoneAllIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  CalendarToday as CalendarTodayIcon,
  ViewList as ViewListIcon,
  Schedule as ScheduleIcon,
  People as PeopleIcon,
  Analytics as AnalyticsIcon,
  Timeline as TimelineIcon,
  TrendingUp as TrendingUpIcon,
  EventNote as EventNoteIcon,
  MoreVert as MoreVertIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  Upload as UploadIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  DragIndicator as DragIndicatorIcon
} from '@mui/icons-material';
import { updateSchedule, deleteSchedule, batchScheduleAction, createSchedule } from '../../services/scheduleService';
import AdminDragDropGrid from '../schedule/AdminDragDropGrid';

const ScheduleManagement = ({
  users,
  fetchSchedules,
  schedules,
  filteredSchedules,
  setFilteredSchedules,
  statusFilter,
  setStatusFilter,
  dateRangeFilter,
  setDateRangeFilter,
  showNotification,
  api,
  isMobile
}) => {
  const theme = useTheme();
  const [schedulePage, setSchedulePage] = useState(0);
  const [scheduleRowsPerPage, setScheduleRowsPerPage] = useState(10);
  const [openScheduleDialog, setOpenScheduleDialog] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [openFilterDialog, setOpenFilterDialog] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [scheduleForm, setScheduleForm] = useState({
    userId: '',
    date: new Date(),
    startTime: '',
    endTime: '',
    status: 'pending',
    notes: '',
    platform: ''
  });
  const [viewMode, setViewMode] = useState('dragdrop'); // 'dragdrop', 'week' hoặc 'list'
  const [currentWeek, setCurrentWeek] = useState(getWeekDates(new Date()));
  
  // State cho chức năng xử lý hàng loạt
  const [selectedSchedules, setSelectedSchedules] = useState([]);
  const [processingBatch, setProcessingBatch] = useState(false);

  // Stats tính toán
  const stats = {
    total: schedules.length,
    pending: schedules.filter(s => s.status === 'pending').length,
    approved: schedules.filter(s => s.status === 'approved').length,
    rejected: schedules.filter(s => s.status === 'rejected').length,
    completed: schedules.filter(s => s.status === 'completed').length,
    shopee: schedules.filter(s => s.platform === 'shopee').length,
    tiktok: schedules.filter(s => s.platform === 'tiktok').length
  };

  // Lấy các ngày trong tuần từ ngày bắt đầu
  function getWeekDates(startDate) {
    const dates = [];
    const currentDate = new Date(startDate);
    
    // Đặt giờ là 12:00 trưa để tránh vấn đề về múi giờ
    currentDate.setHours(12, 0, 0, 0);
    
    const day = currentDate.getDay();
    const diff = currentDate.getDate() - day + (day === 0 ? -6 : 1); // Điều chỉnh về thứ Hai
    currentDate.setDate(diff);
    
    // Log để debug
    console.log(`Week starts at: ${currentDate.toISOString()} (${currentDate.toLocaleDateString('vi-VN')})`);
    
    for (let i = 0; i < 7; i++) {
      const date = new Date(currentDate);
      dates.push(date);
      currentDate.setDate(currentDate.getDate() + 1);
      
      // Log từng ngày trong tuần
      console.log(`Day ${i+1}: ${date.toISOString()} (${date.toLocaleDateString('vi-VN')})`);
    }
    return dates;
  }

  // Di chuyển đến tuần trước
  const goToPreviousWeek = () => {
    const firstDayOfWeek = new Date(currentWeek[0]);
    firstDayOfWeek.setDate(firstDayOfWeek.getDate() - 7);
    const newWeekDates = getWeekDates(firstDayOfWeek);
    setCurrentWeek(newWeekDates);
    
    // Cập nhật date range filter
    setDateRangeFilter({
      startDate: newWeekDates[0],
      endDate: newWeekDates[6]
    });
  };

  // Di chuyển đến tuần sau
  const goToNextWeek = () => {
    const firstDayOfWeek = new Date(currentWeek[0]);
    firstDayOfWeek.setDate(firstDayOfWeek.getDate() + 7);
    const newWeekDates = getWeekDates(firstDayOfWeek);
    setCurrentWeek(newWeekDates);
    
    // Cập nhật date range filter
    setDateRangeFilter({
      startDate: newWeekDates[0],
      endDate: newWeekDates[6]
    });
  };

  // Cập nhật dateRangeFilter khi currentWeek thay đổi
  useEffect(() => {
    if (currentWeek.length > 0) {
      fetchSchedules();
    }
  }, [dateRangeFilter]);

  // Lọc lịch theo trạng thái và search
  const filterSchedules = useCallback((scheduleList, status, search = '') => {
    let filtered = scheduleList;
    
    // Filter by status
    if (status !== 'all') {
      filtered = filtered.filter(schedule => schedule.status === status);
    }
    
    // Filter by search term
    if (search) {
      filtered = filtered.filter(schedule => {
        const userName = getScheduleUserName(schedule.userId).toLowerCase();
        const notes = (schedule.notes || '').toLowerCase();
        const platform = (schedule.platform || '').toLowerCase();
        return userName.includes(search.toLowerCase()) || 
               notes.includes(search.toLowerCase()) ||
               platform.includes(search.toLowerCase());
      });
    }
    
    setFilteredSchedules(filtered);
  }, [setFilteredSchedules]);

  // Xử lý thay đổi trạng thái filter
  const handleStatusFilterChange = (event) => {
    const status = event.target.value;
    setStatusFilter(status);
    filterSchedules(schedules, status, searchTerm);
  };

  // Xử lý search
  const handleSearchChange = (event) => {
    const search = event.target.value;
    setSearchTerm(search);
    filterSchedules(schedules, statusFilter, search);
  };

  // Clear search
  const clearSearch = () => {
    setSearchTerm('');
    filterSchedules(schedules, statusFilter, '');
  };

  // Xử lý thay đổi khoảng thời gian
  const handleDateRangeChange = (type, value) => {
    setDateRangeFilter(prev => ({
      ...prev,
      [type]: value
    }));
  };

  // Áp dụng filter và đóng dialog
  const applyFilters = () => {
    fetchSchedules();
    setOpenFilterDialog(false);
  };

  // Refresh data
  const handleRefresh = async () => {
    setIsLoading(true);
    try {
      await fetchSchedules();
      showNotification('Đã cập nhật dữ liệu mới nhất', 'success');
    } catch (error) {
      showNotification('Có lỗi khi cập nhật dữ liệu', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  // Mở dialog chỉnh sửa lịch
  const handleEditSchedule = (schedule) => {
    setSelectedSchedule(schedule);
    setScheduleForm({
      userId: schedule.userId._id || schedule.userId,
      date: new Date(schedule.date),
      startTime: schedule.startTime,
      endTime: schedule.endTime,
      status: schedule.status,
      notes: schedule.notes || '',
      platform: schedule.platform || ''
    });
    setOpenScheduleDialog(true);
  };

  // Đóng dialog chỉnh sửa lịch
  const handleCloseScheduleDialog = () => {
    setOpenScheduleDialog(false);
    setSelectedSchedule(null);
    setScheduleForm({
      userId: '',
      date: new Date(),
      startTime: '',
      endTime: '',
      status: 'pending',
      notes: '',
      platform: ''
    });
  };

  // Xử lý thay đổi form lịch
  const handleScheduleFormChange = (e) => {
    const { name, value } = e.target;
    setScheduleForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Cập nhật lịch
  const handleUpdateSchedule = async () => {
    try {
      setIsLoading(true);
      // Đảm bảo ngày được chuẩn hóa về giờ chính xác trước khi gửi đi
      const normalizedDate = new Date(scheduleForm.date);
      normalizedDate.setHours(12, 0, 0, 0);
      
      console.log(`Cập nhật lịch: ID=${selectedSchedule._id}, ngày=${normalizedDate.toISOString()}, giờ=${scheduleForm.startTime}-${scheduleForm.endTime}`);
      
      const result = await updateSchedule(selectedSchedule._id, {
        date: normalizedDate.toISOString(),
        startTime: scheduleForm.startTime,
        endTime: scheduleForm.endTime,
        status: scheduleForm.status,
        notes: scheduleForm.notes,
        platform: scheduleForm.platform
      });
      
      if (result.success) {
        showNotification('Cập nhật lịch livestream thành công');
        handleCloseScheduleDialog();
        fetchSchedules();
      } else {
        showNotification(result.error?.message || 'Không thể cập nhật lịch livestream', 'error');
      }
    } catch (error) {
      console.error('Lỗi khi cập nhật lịch:', error);
      showNotification('Không thể cập nhật lịch livestream', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  // Xử lý xóa lịch
  const handleDeleteSchedule = async (id) => {
    if (window.confirm('Bạn có chắc chắn muốn xóa lịch livestream này?')) {
      try {
        setIsLoading(true);
        const result = await deleteSchedule(id);
        
        if (result.success) {
          showNotification('Xóa lịch livestream thành công');
          fetchSchedules();
        } else {
          showNotification(result.error?.message || 'Không thể xóa lịch livestream', 'error');
        }
      } catch (error) {
        console.error('Lỗi khi xóa lịch:', error);
        showNotification('Không thể xóa lịch livestream', 'error');
      } finally {
        setIsLoading(false);
      }
    }
  };

  // Cập nhật trạng thái nhanh
  const handleQuickStatusUpdate = async (id, status) => {
    try {
      setIsLoading(true);
      const result = await updateSchedule(id, { status });
      
      if (result.success) {
        const statusText = status === 'approved' ? 'Đã duyệt' : 
                          status === 'rejected' ? 'Từ chối' : 
                          status === 'completed' ? 'Đã hoàn thành' : 'Chờ duyệt';
        
        showNotification(`Cập nhật trạng thái thành ${statusText}`);
        fetchSchedules();
      } else {
        showNotification(result.error?.message || 'Không thể cập nhật trạng thái', 'error');
      }
    } catch (error) {
      console.error('Lỗi khi cập nhật trạng thái:', error);
      showNotification('Không thể cập nhật trạng thái', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  // Hiển thị tên người dùng
  const getScheduleUserName = (userId) => {
    const id = typeof userId === 'object' ? userId._id : userId;
    const user = users.find(u => u._id === id);
    return user ? user.fullName : 'N/A';
  };

  // Xử lý phân trang cho lịch
  const handleSchedulePageChange = (event, newPage) => {
    setSchedulePage(newPage);
  };

  const handleScheduleRowsPerPageChange = (event) => {
    setScheduleRowsPerPage(parseInt(event.target.value, 10));
    setSchedulePage(0);
  };
  
  // Chọn/bỏ chọn lịch đăng ký
  const handleSelectSchedule = (scheduleId) => {
    setSelectedSchedules(prev => {
      if (prev.includes(scheduleId)) {
        return prev.filter(id => id !== scheduleId);
      } else {
        return [...prev, scheduleId];
      }
    });
  };

  // Chọn/bỏ chọn tất cả lịch hiện tại
  const handleSelectAllSchedules = () => {
    if (selectedSchedules.length === filteredSchedules.length) {
      // Nếu đã chọn tất cả thì bỏ chọn
      setSelectedSchedules([]);
    } else {
      // Chọn tất cả
      setSelectedSchedules(filteredSchedules.map(schedule => schedule._id));
    }
  };
  
  // Xử lý hành động hàng loạt: duyệt, từ chối, hoàn thành, xóa
  const handleBatchAction = async (action) => {
    if (selectedSchedules.length === 0) {
      showNotification('Vui lòng chọn ít nhất một lịch để thực hiện hành động', 'warning');
      return;
    }
    
    // Xác nhận trước khi thực hiện
    let confirmMessage = '';
    switch (action) {
      case 'approve':
        confirmMessage = `Bạn có chắc muốn duyệt ${selectedSchedules.length} lịch đã chọn?`;
        break;
      case 'reject':
        confirmMessage = `Bạn có chắc muốn từ chối ${selectedSchedules.length} lịch đã chọn?`;
        break;
      case 'complete':
        confirmMessage = `Bạn có chắc muốn đánh dấu hoàn thành ${selectedSchedules.length} lịch đã chọn?`;
        break;
      case 'delete':
        confirmMessage = `Bạn có chắc muốn xóa ${selectedSchedules.length} lịch đã chọn?`;
        break;
    }
    
    if (!window.confirm(confirmMessage)) {
      return;
    }
    
    try {
      setProcessingBatch(true);
      
      const result = await batchScheduleAction(selectedSchedules, action);
      
      if (result.success) {
        showNotification(`Đã ${action === 'approve' ? 'duyệt' : action === 'reject' ? 'từ chối' : action === 'complete' ? 'hoàn thành' : 'xóa'} ${selectedSchedules.length} lịch`, 'success');
        
        // Reset lựa chọn và tải lại dữ liệu
        setSelectedSchedules([]);
        fetchSchedules();
      } else {
        showNotification(result.error?.message || 'Không thể thực hiện hành động hàng loạt', 'error');
      }
    } catch (error) {
      console.error('Lỗi khi xử lý hàng loạt:', error);
      showNotification('Không thể thực hiện hành động hàng loạt', 'error');
    } finally {
      setProcessingBatch(false);
    }
  };
  
  // Kiểm tra xem một lịch có được chọn hay không
  const isScheduleSelected = (scheduleId) => {
    return selectedSchedules.includes(scheduleId);
  };

  // Lấy màu sắc dựa trên trạng thái
  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return theme.palette.warning.main;
      case 'approved':
        return theme.palette.success.main;
      case 'rejected':
        return theme.palette.error.main;
      case 'completed':
        return theme.palette.info.main;
      default:
        return theme.palette.grey[500];
    }
  };

  // Nhóm lịch theo ngày trong tuần
  const getSchedulesByDate = (date) => {
    // Kiểm tra nếu filteredSchedules không tồn tại hoặc không phải array
    if (!filteredSchedules || !Array.isArray(filteredSchedules)) {
      return [];
    }

    // Chuẩn hóa ngày đầu vào để đảm bảo thời gian chính xác khi so sánh
    const normalizedDate = new Date(date);
    normalizedDate.setHours(12, 0, 0, 0);
    const dateStr = normalizedDate.toISOString().split('T')[0];
    
    return filteredSchedules.filter(schedule => {
      if (!schedule || !schedule.date) return false;
      
      // Chuẩn hóa ngày của lịch để so sánh chính xác
      const scheduleDateObj = new Date(schedule.date);
      const normalizedScheduleDate = new Date(scheduleDateObj.getFullYear(), scheduleDateObj.getMonth(), scheduleDateObj.getDate(), 12, 0, 0);
      const scheduleStr = normalizedScheduleDate.toISOString().split('T')[0];
      
      return scheduleStr === dateStr;
    });
  };

  // Format giờ
  const formatTime = (timeString) => {
    if (!timeString) return '';
    return timeString.substring(0, 5); // Chỉ hiển thị HH:MM
  };

  // Helper function để format platform name
  const getPlatformName = (platform) => {
    switch (platform) {
      case 'shopee': return '🛒 Shopee';
      case 'tiktok': return '🎵 TikTok';
      default: return platform || '-';
    }
  };

  // Stats Cards Component
  const StatsCards = () => (
    <Grid container spacing={3} sx={{ mb: 4 }}>
      <Grid item xs={6} md={3}>
        <Card 
          elevation={0}
          sx={{ 
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            borderRadius: 3,
            position: 'relative',
            overflow: 'hidden'
          }}
        >
          <CardContent sx={{ position: 'relative', zIndex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="h4" fontWeight="bold">
                  {stats.total}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Tổng lịch
                </Typography>
              </Box>
              <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 56, height: 56 }}>
                <ScheduleIcon fontSize="large" />
              </Avatar>
            </Box>
          </CardContent>
          <Box sx={{ 
            position: 'absolute', 
            bottom: -20, 
            right: -20, 
            opacity: 0.1,
            transform: 'rotate(15deg)'
          }}>
            <EventNoteIcon sx={{ fontSize: 100 }} />
          </Box>
        </Card>
      </Grid>

      <Grid item xs={6} md={3}>
        <Card 
          elevation={0}
          sx={{ 
            background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
            color: 'white',
            borderRadius: 3,
            position: 'relative',
            overflow: 'hidden'
          }}
        >
          <CardContent sx={{ position: 'relative', zIndex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="h4" fontWeight="bold">
                  {stats.pending}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Chờ duyệt
                </Typography>
              </Box>
              <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 56, height: 56 }}>
                <TimelineIcon fontSize="large" />
              </Avatar>
            </Box>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={6} md={3}>
        <Card 
          elevation={0}
          sx={{ 
            background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
            color: 'white',
            borderRadius: 3,
            position: 'relative',
            overflow: 'hidden'
          }}
        >
          <CardContent sx={{ position: 'relative', zIndex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="h4" fontWeight="bold">
                  {stats.approved}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Đã duyệt
                </Typography>
              </Box>
              <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 56, height: 56 }}>
                <CheckCircleIcon fontSize="large" />
              </Avatar>
            </Box>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={6} md={3}>
        <Card 
          elevation={0}
          sx={{ 
            background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
            color: 'white',
            borderRadius: 3,
            position: 'relative',
            overflow: 'hidden'
          }}
        >
          <CardContent sx={{ position: 'relative', zIndex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="h4" fontWeight="bold">
                  {stats.completed}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Hoàn thành
                </Typography>
              </Box>
              <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 56, height: 56 }}>
                <DoneAllIcon fontSize="large" />
              </Avatar>
            </Box>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );

  return (
    <Container maxWidth="xl">
      <AppBar position="static" sx={{ mb: 4, bgcolor: 'white', boxShadow: 1 }}>
        <Toolbar>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Quản lý lịch đăng ký livestream
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <TextField
              label="Tìm kiếm lịch"
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1 }} />,
                endAdornment: searchTerm && (
                  <IconButton onClick={clearSearch} size="small">
                    <ClearIcon />
                  </IconButton>
                )
              }}
            />
            <FormControl sx={{ minWidth: 120 }}>
              <InputLabel>Trạng thái</InputLabel>
              <Select
                value={statusFilter}
                onChange={handleStatusFilterChange}
                label="Trạng thái"
                size="small"
              >
                <MenuItem value="all">Tất cả</MenuItem>
                <MenuItem value="pending">Chờ duyệt</MenuItem>
                <MenuItem value="approved">Đã duyệt</MenuItem>
                <MenuItem value="rejected">Từ chối</MenuItem>
                <MenuItem value="completed">Đã hoàn thành</MenuItem>
              </Select>
            </FormControl>
            <ButtonGroup variant="outlined" size="small">
              <IconButton onClick={goToPreviousWeek}>
                <ChevronLeftIcon />
              </IconButton>
              <IconButton onClick={goToNextWeek}>
                <ChevronRightIcon />
              </IconButton>
            </ButtonGroup>
            <Button
              variant="contained"
              startIcon={<RefreshIcon />}
              onClick={handleRefresh}
              disabled={isLoading}
            >
              {isLoading ? <LinearProgress color="primary" size={20} /> : 'Làm mới'}
            </Button>
          </Box>
        </Toolbar>
      </AppBar>

      <StatsCards />

      <Paper elevation={3} sx={{ p: isMobile ? 2 : 3, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: isMobile ? 'wrap' : 'nowrap' }}>
          <Typography variant={isMobile ? "subtitle1" : "h6"} sx={{ mb: isMobile ? 1 : 0 }}>
            {viewMode === 'dragdrop' ? '🖱️ Kéo thả lịch live' : viewMode === 'week' ? '📅 Lịch tuần' : '📋 Danh sách'}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: isMobile ? 'space-between' : 'flex-end', width: isMobile ? '100%' : 'auto' }}>
            <ButtonGroup variant="outlined" size="small" sx={{ mr: 1 }}>
              <Tooltip title="Kéo thả">
                <IconButton 
                  onClick={() => setViewMode('dragdrop')} 
                  size={isMobile ? "small" : "medium"}
                  color={viewMode === 'dragdrop' ? 'primary' : 'default'}
                >
                  <DragIndicatorIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Lịch tuần">
                <IconButton 
                  onClick={() => setViewMode('week')} 
                  size={isMobile ? "small" : "medium"}
                  color={viewMode === 'week' ? 'primary' : 'default'}
                >
                  <CalendarTodayIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Danh sách">
                <IconButton 
                  onClick={() => setViewMode('list')} 
                  size={isMobile ? "small" : "medium"}
                  color={viewMode === 'list' ? 'primary' : 'default'}
                >
                  <ViewListIcon />
                </IconButton>
              </Tooltip>
            </ButtonGroup>
            <Button 
              variant="contained" 
              color="success" 
              size="small"
              disabled={processingBatch}
              onClick={() => handleBatchAction('approve')}
              startIcon={<CheckCircleIcon />}
              sx={{ mr: 1 }}
            >
              Duyệt ({selectedSchedules.length})
            </Button>
            <Button 
              variant="contained" 
              color="error" 
              size="small"
              disabled={processingBatch}
              onClick={() => handleBatchAction('reject')}
              startIcon={<CancelIcon />}
              sx={{ mr: 1 }}
            >
              Từ chối ({selectedSchedules.length})
            </Button>
            <Button 
              variant="contained" 
              color="primary" 
              size="small"
              disabled={processingBatch}
              onClick={() => handleBatchAction('complete')}
              startIcon={<DoneAllIcon />}
              sx={{ mr: 1 }}
            >
              Hoàn thành ({selectedSchedules.length})
            </Button>
            <Button 
              variant="contained" 
              color="secondary" 
              size="small"
              disabled={processingBatch}
              onClick={() => handleBatchAction('delete')}
              startIcon={<DeleteIcon />}
            >
              Xóa ({selectedSchedules.length})
            </Button>
          </Box>
        </Box>
        
        {viewMode === 'dragdrop' ? (
          <AdminDragDropGrid
            schedules={filteredSchedules}
            users={users}
            weekDates={currentWeek}
            onEdit={(scheduleId, schedule) => {
              handleEditSchedule(schedule);
            }}
            onDelete={handleDeleteSchedule}
            onAdd={async (scheduleData) => {
              // Create new schedule via API
              try {
                setIsLoading(true);
                
                // Chuẩn hóa date về ISO string
                const normalizedDate = new Date(scheduleData.date);
                normalizedDate.setHours(12, 0, 0, 0);
                
                const result = await createSchedule({
                  ...scheduleData,
                  date: normalizedDate.toISOString()
                });
                
                if (result.success) {
                  showNotification('Đã thêm lịch thành công!', 'success');
                  fetchSchedules();
                } else {
                  showNotification(result.error?.message || 'Lỗi khi thêm lịch', 'error');
                }
              } catch (error) {
                console.error('Error adding schedule:', error);
                showNotification('Lỗi khi thêm lịch', 'error');
              } finally {
                setIsLoading(false);
              }
            }}
            onMove={async (scheduleId, newData) => {
              // Move schedule to new slot
              try {
                setIsLoading(true);
                
                // Chuẩn hóa date về ISO string
                const normalizedDate = new Date(newData.date);
                normalizedDate.setHours(12, 0, 0, 0);
                
                const result = await updateSchedule(scheduleId, {
                  ...newData,
                  date: normalizedDate.toISOString()
                });
                
                if (result.success) {
                  showNotification('Đã di chuyển lịch thành công!', 'success');
                  fetchSchedules();
                } else {
                  showNotification(result.error?.message || 'Không thể di chuyển lịch', 'error');
                }
              } catch (error) {
                console.error('Error moving schedule:', error);
                showNotification('Không thể di chuyển lịch', 'error');
              } finally {
                setIsLoading(false);
              }
            }}
          />
        ) : viewMode === 'week' ? (
          <>
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6" sx={{ mx: 2, fontWeight: 600 }}>
                {`${currentWeek[0].toLocaleDateString('vi-VN')} - ${currentWeek[6].toLocaleDateString('vi-VN')}`}
              </Typography>
            </Box>

            {selectedSchedules.length > 0 && (
              <Slide direction="down" in={selectedSchedules.length > 0}>
                <Paper 
                  elevation={2}
                  sx={{ 
                    mb: 3, 
                    p: 2, 
                    bgcolor: 'primary.50',
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'primary.200'
                  }}
                >
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    <Chip 
                      label={`${selectedSchedules.length} lịch đã chọn`}
                      color="primary"
                      size="small"
                      sx={{ mb: 1 }}
                    />
                    <Button 
                      variant="contained" 
                      color="success" 
                      size="small"
                      disabled={processingBatch}
                      onClick={() => handleBatchAction('approve')}
                      startIcon={<CheckCircleIcon />}
                      sx={{ mb: 1 }}
                    >
                      Duyệt
                    </Button>
                    <Button 
                      variant="contained" 
                      color="error" 
                      size="small"
                      disabled={processingBatch}
                      onClick={() => handleBatchAction('reject')}
                      startIcon={<CancelIcon />}
                      sx={{ mb: 1 }}
                    >
                      Từ chối
                    </Button>
                    <Button 
                      variant="contained" 
                      color="info" 
                      size="small"
                      disabled={processingBatch}
                      onClick={() => handleBatchAction('complete')}
                      startIcon={<DoneAllIcon />}
                      sx={{ mb: 1 }}
                    >
                      Hoàn thành
                    </Button>
                    <Button 
                      variant="outlined" 
                      color="error" 
                      size="small"
                      disabled={processingBatch}
                      onClick={() => handleBatchAction('delete')}
                      startIcon={<DeleteIcon />}
                      sx={{ mb: 1 }}
                    >
                      Xóa
                    </Button>
                  </Stack>
                </Paper>
              </Slide>
            )}
            
            <Grid container spacing={isMobile ? 1 : 2}>
              {currentWeek.map((date, index) => (
                <Grid item xs={isMobile ? (index === 0 || index === 6 ? 6 : 4) : 12/7} key={index}>
                  <Zoom in={true} timeout={300 + index * 100}>
                    <Paper 
                      elevation={3} 
                      sx={{ 
                        height: '100%', 
                        borderRadius: 2,
                        overflow: 'hidden',
                        backgroundColor: date.toDateString() === new Date().toDateString() 
                          ? alpha(theme.palette.primary.main, 0.1) 
                          : 'white',
                        border: date.toDateString() === new Date().toDateString() 
                          ? `2px solid ${theme.palette.primary.main}` 
                          : '1px solid #e0e0e0',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: theme.shadows[6]
                        }
                      }}
                    >
                      <Box sx={{ 
                        p: 1, 
                        bgcolor: date.toDateString() === new Date().toDateString() 
                          ? theme.palette.primary.main 
                          : theme.palette.grey[100],
                        color: date.toDateString() === new Date().toDateString() 
                          ? 'white' 
                          : 'text.primary'
                      }}>
                        <Typography variant="subtitle2" sx={{ textAlign: 'center', fontWeight: 'bold' }}>
                          {['CN', 'T2', 'T3', 'T4', 'T5', 'T6', 'T7'][date.getDay()]}
                        </Typography>
                        <Typography variant="h6" sx={{ textAlign: 'center', fontWeight: 'bold' }}>
                          {date.getDate()}
                        </Typography>
                        <Typography variant="caption" sx={{ textAlign: 'center', display: 'block' }}>
                          Tháng {date.getMonth() + 1}
                        </Typography>
                      </Box>
                      
                      <Box sx={{ p: 1, minHeight: isMobile ? 180 : 270, maxHeight: isMobile ? 180 : 270, overflowY: 'auto' }}>
                        {getSchedulesByDate(date).length > 0 ? (
                          <Stack spacing={1}>
                            {getSchedulesByDate(date).map((schedule, idx) => (
                              <Zoom in={true} timeout={500 + idx * 100} key={schedule._id}>
                                <Card 
                                  elevation={isScheduleSelected(schedule._id) ? 3 : 1}
                                  sx={{ 
                                    borderLeft: `4px solid ${getStatusColor(schedule.status)}`,
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease',
                                    bgcolor: isScheduleSelected(schedule._id) 
                                      ? alpha(theme.palette.primary.main, 0.08) 
                                      : 'white',
                                    '&:hover': { 
                                      boxShadow: theme.shadows[4],
                                      transform: 'translateX(2px)'
                                    }
                                  }}
                                  onClick={() => handleSelectSchedule(schedule._id)}
                                >
                                  <CardContent sx={{ py: 1, px: 1.5, '&:last-child': { pb: 1 } }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                                      <Typography variant="caption" sx={{ fontWeight: 'bold', flex: 1 }}>
                                        {getScheduleUserName(schedule.userId)}
                                      </Typography>
                                      <Checkbox 
                                        checked={isScheduleSelected(schedule._id)} 
                                        size="small"
                                        onClick={(e) => e.stopPropagation()}
                                        onChange={(e) => {
                                          e.stopPropagation();
                                          handleSelectSchedule(schedule._id);
                                        }}
                                      />
                                    </Box>
                                    
                                    <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.5 }}>
                                      <ScheduleIcon sx={{ fontSize: 12, color: 'text.secondary' }} />
                                      <Typography variant="caption" sx={{ fontWeight: 500 }}>
                                        {formatTime(schedule.startTime)} - {formatTime(schedule.endTime)}
                                      </Typography>
                                    </Stack>

                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                      <Typography variant="caption" sx={{ 
                                        px: 0.5, 
                                        py: 0.2, 
                                        bgcolor: schedule.platform === 'shopee' ? '#EE4D2D' : '#000',
                                        color: 'white',
                                        borderRadius: 1,
                                        fontSize: '0.7rem'
                                      }}>
                                        {getPlatformName(schedule.platform)}
                                      </Typography>
                                      
                                      <Chip
                                        size="small"
                                        label={
                                          schedule.status === 'pending' ? 'Chờ' :
                                          schedule.status === 'approved' ? 'Duyệt' :
                                          schedule.status === 'rejected' ? 'Từ chối' : 'Xong'
                                        }
                                        sx={{
                                          bgcolor: getStatusColor(schedule.status),
                                          color: 'white',
                                          fontSize: '0.65rem',
                                          height: 18
                                        }}
                                      />
                                    </Box>

                                    {!isMobile && schedule.notes && (
                                      <Typography variant="caption" sx={{ 
                                        display: 'block', 
                                        color: 'text.secondary', 
                                        mt: 0.5,
                                        fontStyle: 'italic'
                                      }}>
                                        {schedule.notes.length > 30 ? `${schedule.notes.substring(0, 30)}...` : schedule.notes}
                                      </Typography>
                                    )}
                                  </CardContent>

                                  <CardActions sx={{ p: 0.5, justifyContent: 'flex-end', minHeight: 'auto' }}>
                                    {schedule.status === 'pending' && (
                                      <Stack direction="row" spacing={0.5}>
                                        <Tooltip title="Duyệt">
                                          <IconButton 
                                            size="small" 
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleQuickStatusUpdate(schedule._id, 'approved');
                                            }}
                                            sx={{ p: 0.5 }}
                                          >
                                            <CheckCircleIcon fontSize="small" sx={{ color: theme.palette.success.main }} />
                                          </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Từ chối">
                                          <IconButton 
                                            size="small" 
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleQuickStatusUpdate(schedule._id, 'rejected');
                                            }}
                                            sx={{ p: 0.5 }}
                                          >
                                            <CancelIcon fontSize="small" sx={{ color: theme.palette.error.main }} />
                                          </IconButton>
                                        </Tooltip>
                                      </Stack>
                                    )}
                                    {schedule.status === 'approved' && (
                                      <Tooltip title="Đánh dấu hoàn thành">
                                        <IconButton 
                                          size="small" 
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleQuickStatusUpdate(schedule._id, 'completed');
                                          }}
                                          sx={{ p: 0.5 }}
                                        >
                                          <DoneAllIcon fontSize="small" sx={{ color: theme.palette.info.main }} />
                                        </IconButton>
                                      </Tooltip>
                                    )}
                                    <Tooltip title="Chỉnh sửa">
                                      <IconButton 
                                        size="small" 
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleEditSchedule(schedule);
                                        }}
                                        sx={{ p: 0.5 }}
                                      >
                                        <EditIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Xóa">
                                      <IconButton 
                                        size="small" 
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleDeleteSchedule(schedule._id);
                                        }}
                                        sx={{ p: 0.5 }}
                                      >
                                        <DeleteIcon fontSize="small" sx={{ color: theme.palette.error.main }} />
                                      </IconButton>
                                    </Tooltip>
                                  </CardActions>
                                </Card>
                              </Zoom>
                            ))}
                          </Stack>
                        ) : (
                          <Box sx={{ 
                            display: 'flex', 
                            flexDirection: 'column',
                            alignItems: 'center', 
                            justifyContent: 'center',
                            height: '100%',
                            color: 'text.disabled'
                          }}>
                            <EventNoteIcon sx={{ fontSize: 40, mb: 1, opacity: 0.5 }} />
                            <Typography variant="caption" sx={{ textAlign: 'center' }}>
                              Không có lịch
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </Paper>
                  </Zoom>
                </Grid>
              ))}
            </Grid>
          </>
        ) : (
          <>
            <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 2 }}>
              <Table size={isMobile ? "small" : "medium"} sx={{ minWidth: isMobile ? 500 : 650 }}>
                <TableHead>
                  <TableRow sx={{ bgcolor: theme.palette.grey[50] }}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        indeterminate={selectedSchedules.length > 0 && selectedSchedules.length < filteredSchedules.length}
                        checked={filteredSchedules.length > 0 && selectedSchedules.length === filteredSchedules.length}
                        onChange={handleSelectAllSchedules}
                      />
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Người dùng</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Ngày</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Bắt đầu</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Kết thúc</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Platform</TableCell>
                    {!isMobile && <TableCell sx={{ fontWeight: 600 }}>Ghi chú</TableCell>}
                    <TableCell sx={{ fontWeight: 600 }}>Trạng thái</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>Thao tác</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredSchedules.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={isMobile ? 7 : 8} align="center" sx={{ py: 6 }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', color: 'text.disabled' }}>
                          <ScheduleIcon sx={{ fontSize: 64, mb: 2, opacity: 0.5 }} />
                          <Typography variant="h6" gutterBottom>
                            Không có lịch nào
                          </Typography>
                          <Typography variant="body2">
                            {searchTerm ? 'Không tìm thấy lịch phù hợp với từ khóa tìm kiếm' : 'Chưa có lịch đăng ký nào trong hệ thống'}
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredSchedules
                      .slice(schedulePage * scheduleRowsPerPage, schedulePage * scheduleRowsPerPage + scheduleRowsPerPage)
                      .map((schedule, index) => (
                        <TableRow 
                          key={schedule._id}
                          selected={isScheduleSelected(schedule._id)}
                          onClick={() => handleSelectSchedule(schedule._id)}
                          hover
                          sx={{ 
                            cursor: 'pointer',
                            '&:hover': {
                              bgcolor: alpha(theme.palette.primary.main, 0.04)
                            }
                          }}
                        >
                          <TableCell padding="checkbox">
                            <Checkbox checked={isScheduleSelected(schedule._id)} />
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Avatar sx={{ width: 32, height: 32, mr: 1, bgcolor: theme.palette.primary.main }}>
                                {getScheduleUserName(schedule.userId).charAt(0)}
                              </Avatar>
                              <Typography variant="body2" fontWeight={500}>
                                {getScheduleUserName(schedule.userId)}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {new Date(schedule.date).toLocaleDateString('vi-VN')}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={formatTime(schedule.startTime)}
                              size="small"
                              variant="outlined"
                              color="primary"
                            />
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={formatTime(schedule.endTime)}
                              size="small"
                              variant="outlined"
                              color="secondary"
                            />
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={getPlatformName(schedule.platform)}
                              size="small"
                              sx={{
                                bgcolor: schedule.platform === 'shopee' ? '#EE4D2D' : '#000',
                                color: 'white'
                              }}
                            />
                          </TableCell>
                          {!isMobile && (
                            <TableCell sx={{ maxWidth: 200 }}>
                              {schedule.notes ? (
                                <Tooltip title={schedule.notes}>
                                  <Typography variant="body2" sx={{ 
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                  }}>
                                    {schedule.notes}
                                  </Typography>
                                </Tooltip>
                              ) : (
                                <Typography variant="body2" color="text.disabled">-</Typography>
                              )}
                            </TableCell>
                          )}
                          <TableCell>
                            <Chip 
                              label={
                                schedule.status === 'pending' ? 'Chờ duyệt' :
                                schedule.status === 'approved' ? 'Đã duyệt' :
                                schedule.status === 'rejected' ? 'Từ chối' : 'Đã hoàn thành'
                              }
                              size="small"
                              sx={{
                                bgcolor: getStatusColor(schedule.status),
                                color: 'white',
                                fontWeight: 500
                              }}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                              {schedule.status === 'pending' && (
                                <>
                                  <Tooltip title="Duyệt">
                                    <IconButton 
                                      size="small" 
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleQuickStatusUpdate(schedule._id, 'approved');
                                      }}
                                      sx={{ color: theme.palette.success.main }}
                                    >
                                      <CheckCircleIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Từ chối">
                                    <IconButton 
                                      size="small" 
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleQuickStatusUpdate(schedule._id, 'rejected');
                                      }}
                                      sx={{ color: theme.palette.error.main }}
                                    >
                                      <CancelIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </>
                              )}
                              {schedule.status === 'approved' && (
                                <Tooltip title="Đánh dấu hoàn thành">
                                  <IconButton 
                                    size="small" 
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleQuickStatusUpdate(schedule._id, 'completed');
                                    }}
                                    sx={{ color: theme.palette.info.main }}
                                  >
                                    <DoneAllIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                              <Tooltip title="Chỉnh sửa">
                                <IconButton 
                                  size="small" 
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleEditSchedule(schedule);
                                  }}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Xóa">
                                <IconButton 
                                  size="small" 
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteSchedule(schedule._id);
                                  }}
                                  sx={{ color: theme.palette.error.main }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {filteredSchedules.length > 0 && (
              <TablePagination
                component="div"
                count={filteredSchedules.length}
                page={schedulePage}
                onPageChange={handleSchedulePageChange}
                rowsPerPage={scheduleRowsPerPage}
                onRowsPerPageChange={handleScheduleRowsPerPageChange}
                labelRowsPerPage={isMobile ? "Dòng:" : "Số hàng mỗi trang:"}
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} của ${count}`}
                rowsPerPageOptions={isMobile ? [5, 10, 25] : [10, 25, 50, 100]}
              />
            )}
          </>
        )}
        
        {/* Dialog filter lịch */}
        <Dialog
          open={openFilterDialog}
          onClose={() => setOpenFilterDialog(false)}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>
            Lọc lịch
          </DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2 }}>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Trạng thái</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={handleStatusFilterChange}
                  label="Trạng thái"
                >
                  <MenuItem value="all">Tất cả</MenuItem>
                  <MenuItem value="pending">Chờ duyệt</MenuItem>
                  <MenuItem value="approved">Đã duyệt</MenuItem>
                  <MenuItem value="rejected">Từ chối</MenuItem>
                  <MenuItem value="completed">Đã hoàn thành</MenuItem>
                </Select>
              </FormControl>
              
              <DatePicker
                label="Từ ngày"
                value={dateRangeFilter.startDate}
                onChange={(newValue) => handleDateRangeChange('startDate', newValue)}
                slotProps={{ textField: { fullWidth: true, sx: { mb: 2 } } }}
              />
              
              <DatePicker
                label="Đến ngày"
                value={dateRangeFilter.endDate}
                onChange={(newValue) => handleDateRangeChange('endDate', newValue)}
                slotProps={{ textField: { fullWidth: true } }}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenFilterDialog(false)}>Hủy</Button>
            <Button onClick={applyFilters} variant="contained">
              Áp dụng
            </Button>
          </DialogActions>
        </Dialog>
        
        {/* Dialog chỉnh sửa lịch */}
        <Dialog
          open={openScheduleDialog}
          onClose={handleCloseScheduleDialog}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: { borderRadius: 3 }
          }}
        >
          <DialogTitle sx={{ 
            bgcolor: 'primary.main', 
            color: 'white',
            display: 'flex',
            alignItems: 'center'
          }}>
            <ScheduleIcon sx={{ mr: 1 }} />
            Chỉnh sửa lịch livestream
          </DialogTitle>
          <DialogContent sx={{ mt: 2 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Người dùng</InputLabel>
                  <Select
                    name="userId"
                    value={scheduleForm.userId}
                    onChange={handleScheduleFormChange}
                    label="Người dùng"
                    disabled={selectedSchedule !== null}
                  >
                    {users.map(user => (
                      <MenuItem key={user._id} value={user._id}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Avatar sx={{ width: 24, height: 24, mr: 1, bgcolor: 'primary.main' }}>
                            {user.fullName.charAt(0)}
                          </Avatar>
                          {user.fullName}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <DatePicker
                  label="Ngày"
                  value={scheduleForm.date}
                  onChange={(newValue) => setScheduleForm(prev => ({ ...prev, date: newValue }))}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Giờ bắt đầu"
                  type="time"
                  name="startTime"
                  value={scheduleForm.startTime}
                  onChange={handleScheduleFormChange}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ step: 300 }}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Giờ kết thúc"
                  type="time"
                  name="endTime"
                  value={scheduleForm.endTime}
                  onChange={handleScheduleFormChange}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ step: 300 }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Platform</InputLabel>
                  <Select
                    name="platform"
                    value={scheduleForm.platform}
                    onChange={handleScheduleFormChange}
                    label="Platform"
                  >
                    <MenuItem value="">Chọn platform</MenuItem>
                    <MenuItem value="shopee">
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box sx={{ 
                          width: 20, 
                          height: 20, 
                          bgcolor: '#EE4D2D', 
                          borderRadius: 1, 
                          mr: 1,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <Typography variant="caption" sx={{ color: 'white', fontWeight: 'bold' }}>
                            S
                          </Typography>
                        </Box>
                        🛒 Shopee
                      </Box>
                    </MenuItem>
                    <MenuItem value="tiktok">
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box sx={{ 
                          width: 20, 
                          height: 20, 
                          bgcolor: '#000', 
                          borderRadius: 1, 
                          mr: 1,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <Typography variant="caption" sx={{ color: 'white', fontWeight: 'bold' }}>
                            T
                          </Typography>
                        </Box>
                        🎵 TikTok
                      </Box>
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Trạng thái</InputLabel>
                  <Select
                    name="status"
                    value={scheduleForm.status}
                    onChange={handleScheduleFormChange}
                    label="Trạng thái"
                  >
                    <MenuItem value="pending">
                      <Chip label="Chờ duyệt" size="small" sx={{ bgcolor: theme.palette.warning.main, color: 'white' }} />
                    </MenuItem>
                    <MenuItem value="approved">
                      <Chip label="Đã duyệt" size="small" sx={{ bgcolor: theme.palette.success.main, color: 'white' }} />
                    </MenuItem>
                    <MenuItem value="rejected">
                      <Chip label="Từ chối" size="small" sx={{ bgcolor: theme.palette.error.main, color: 'white' }} />
                    </MenuItem>
                    <MenuItem value="completed">
                      <Chip label="Đã hoàn thành" size="small" sx={{ bgcolor: theme.palette.info.main, color: 'white' }} />
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Ghi chú"
                  name="notes"
                  value={scheduleForm.notes}
                  onChange={handleScheduleFormChange}
                  multiline
                  rows={isMobile ? 2 : 3}
                  placeholder="Thêm ghi chú cho lịch livestream..."
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ p: 3 }}>
            <Button onClick={handleCloseScheduleDialog} variant="outlined">
              Hủy
            </Button>
            <Button 
              onClick={handleUpdateSchedule} 
              variant="contained"
              disabled={isLoading}
              startIcon={isLoading ? <LinearProgress /> : <EditIcon />}
            >
              {isLoading ? 'Đang cập nhật...' : 'Cập nhật'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Loading overlay */}
        {isLoading && (
          <Box sx={{ 
            position: 'fixed', 
            top: 0, 
            left: 0, 
            right: 0, 
            bottom: 0, 
            bgcolor: 'rgba(0,0,0,0.3)', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            zIndex: 9999
          }}>
            <Paper sx={{ p: 3, borderRadius: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
              <LinearProgress sx={{ width: 100 }} />
              <Typography>Đang xử lý...</Typography>
            </Paper>
          </Box>
        )}
      </Paper>
    </Container>
  );
};

export default ScheduleManagement; 